import { useState, useEffect } from "react";
import api from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useNavigate } from "react-router-dom";
import { 
  Users, Plane, Battery, AlertTriangle, 
  CheckCircle, AlertCircle, XCircle, ArrowRight,
  ShieldCheck, ShieldAlert, Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

const StatusSection = ({ title, icon: Icon, items, total, color, onClick }) => {
  const getProgressColor = () => {
    const good = items.find(i => i.type === 'good')?.value || 0;
    const percentage = total > 0 ? (good / total) * 100 : 100;
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 50) return "bg-amber-500";
    return "bg-rose-500";
  };

  const goodCount = items.find(i => i.type === 'good')?.value || 0;
  const percentage = total > 0 ? Math.round((goodCount / total) * 100) : 100;

  return (
    <div 
      className={`p-4 rounded-lg border hover:bg-slate-50 transition-colors ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      data-testid={`compliance-section-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="font-medium text-slate-700">{title}</span>
        </div>
        <span className="text-lg font-bold text-slate-900">{total}</span>
      </div>
      
      <div className="space-y-2">
        <Progress value={percentage} className="h-2" indicatorClassName={getProgressColor()} />
        
        <div className="flex flex-wrap gap-2 mt-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-1">
              {item.type === 'good' && <CheckCircle className="h-3 w-3 text-emerald-500" />}
              {item.type === 'warning' && <AlertCircle className="h-3 w-3 text-amber-500" />}
              {item.type === 'critical' && <XCircle className="h-3 w-3 text-rose-500" />}
              <span className="text-xs text-slate-600">
                {item.value} {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ComplianceStatusWidget = () => {
  const { t } = useLanguage();
  const { activeOrganizationId, isPlatformAdmin } = useOrganizationScope();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // /dashboard/compliance-summary is organization-scoped (ReportsController's RequireOrgId),
  // but this widget sits on Analytics, which stays reachable in the "all organizations" mode.
  // Without an active scope the request would come back 400, so skip it entirely — the same
  // guard Analytics already applies to /submissions/stats.
  const scopeMissing = isPlatformAdmin && !activeOrganizationId;

  useEffect(() => {
    if (scopeMissing) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);
    const fetchData = async () => {
      try {
        const res = await api.get('/dashboard/compliance-summary');
        if (!ignore) setData(res.data);
      } catch (err) {
        if (!ignore) setError(err.message || 'Failed to load');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => { ignore = true; };
  }, [scopeMissing, activeOrganizationId]);

  if (scopeMissing) return null;

  if (loading) {
    return (
      <Card data-testid="compliance-status-widget">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="compliance-status-widget">
        <CardContent className="py-4">
          <div className="text-center text-rose-600">
            <AlertCircle className="h-6 w-6 mx-auto mb-2" />
            <p className="text-sm">Failed to load compliance data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const w = t.complianceWidget || {};
  
  const getOverallStatus = () => {
    if (data.overall_status === 'critical') {
      return {
        icon: ShieldAlert,
        label: w.overallCritical || "Critical Issues",
        color: "text-rose-600",
        bg: "bg-rose-100"
      };
    }
    if (data.overall_status === 'warning') {
      return {
        icon: Shield,
        label: w.overallWarning || "Attention Required",
        color: "text-amber-600",
        bg: "bg-amber-100"
      };
    }
    return {
      icon: ShieldCheck,
      label: w.overallHealthy || "All Clear",
      color: "text-emerald-600",
      bg: "bg-emerald-100"
    };
  };

  const status = getOverallStatus();
  const StatusIcon = status.icon;

  return (
    <Card data-testid="compliance-status-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            {w.title || "Compliance Status"}
          </CardTitle>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status.bg}`}>
            <StatusIcon className={`h-4 w-4 ${status.color}`} />
            <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
          </div>
        </div>
        <p className="text-sm text-slate-500">{w.subtitle || "Fleet and crew status overview"}</p>
      </CardHeader>
      
      <CardContent>
        {/* Summary badges */}
        {(data.critical_issues > 0 || data.warnings > 0) && (
          <div className="flex gap-3 mb-4">
            {data.critical_issues > 0 && (
              <Badge variant="destructive" className="gap-1">
                <XCircle className="h-3 w-3" />
                {data.critical_issues} {w.criticalIssues || "Critical"}
              </Badge>
            )}
            {data.warnings > 0 && (
              <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 bg-amber-50">
                <AlertCircle className="h-3 w-3" />
                {data.warnings} {w.warnings || "Warnings"}
              </Badge>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pilots */}
          <StatusSection
            title={w.pilotsSection || "Pilots"}
            icon={Users}
            total={data.pilots.total}
            color="bg-sky-100 text-sky-600"
            onClick={() => navigate('/pilots')}
            items={[
              { type: 'good', value: data.pilots.valid, label: w.valid || "Valid" },
              { type: 'warning', value: data.pilots.expiring_30d, label: w.expiring || "Expiring" },
              { type: 'critical', value: data.pilots.expired, label: w.expired || "Expired" }
            ]}
          />

          {/* Aircraft */}
          <StatusSection
            title={w.aircraftSection || "Aircraft"}
            icon={Plane}
            total={data.aircraft.total}
            color="bg-indigo-100 text-indigo-600"
            onClick={() => navigate('/aircraft')}
            items={[
              { type: 'good', value: data.aircraft.operational, label: w.operational || "Operational" },
              { type: 'warning', value: data.aircraft.maintenance_due, label: w.maintenanceDue || "Maintenance Due" },
              { type: 'critical', value: data.aircraft.grounded, label: w.grounded || "Grounded" }
            ]}
          />

          {/* Batteries */}
          <StatusSection
            title={w.batteriesSection || "Batteries"}
            icon={Battery}
            total={data.batteries.total}
            color="bg-emerald-100 text-emerald-600"
            onClick={() => navigate('/batteries')}
            items={[
              { type: 'good', value: data.batteries.good, label: w.good || "Good" },
              { type: 'warning', value: data.batteries.warning, label: w.warning || "Warning" },
              { type: 'critical', value: data.batteries.replace, label: w.replace || "Replace" }
            ]}
          />

          {/* Incidents */}
          <StatusSection
            title={w.incidentsSection || "Incidents"}
            icon={AlertTriangle}
            total={data.incidents.total}
            color="bg-amber-100 text-amber-600"
            onClick={() => navigate('/incidents')}
            items={[
              { type: 'good', value: data.incidents.resolved, label: t.safety?.resolved || "Resolved" },
              { type: 'warning', value: data.incidents.open, label: w.openIncidents || "Open" },
              { type: 'critical', value: data.incidents.serious_open, label: w.seriousOpen || "Serious" }
            ]}
          />
        </div>

        {/* View Details Button */}
        <div className="mt-4 pt-4 border-t">
          <Button 
            variant="outline" 
            className="w-full gap-2"
            onClick={() => navigate('/analytics')}
          >
            {w.viewDetails || "View Details"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComplianceStatusWidget;
